'use client';
import { useState } from 'react';
import { ArrowLeft, CheckCircle, MessageCircle, Send } from 'lucide-react';

/**
 * /partner — 夥伴招募頁（R3）
 * 流程：填姓名+WhatsApp 號碼 → POST /partner/apply → WhatsApp deep link 驗證 → 建檔（審核制 pending）
 * 佣金規則：實收 10%（滿 5 萬/年升 12%，每筆可個別調整）、月結、退款沖回。
 * MVP 雙語硬寫（zh 主 / en 對照）；i18n 化留 TODO。
 */

const CF_WORKER_URL = 'https://jkd-whatsapp-verification.jimsbond007.workers.dev';

type Step = 'form' | 'verify' | 'done';

export default function PartnerPage() {
  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { setMsg('請填寫姓名與 WhatsApp 號碼 / Name and WhatsApp number required'); return; }
    setIsSubmitting(true);
    setMsg(null);
    try {
      const res = await fetch(`${CF_WORKER_URL}/partner/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.existing) {
          setMsg(data.message || '此號碼已登記。');
        }
        setDeepLink(data.deepLink);
        setStep('verify');
      } else {
        setMsg(`❌ ${data.error || res.status}`);
      }
    } catch {
      setMsg('提交失敗，請重試 / Submission failed, please retry');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-jkd-black text-white py-14 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => (window.location.href = '/')} className="inline-flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors mb-8">
          <ArrowLeft className="w-5 h-5" /> 返回主頁 / Home
        </button>

        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-2 mb-4 border border-jkd-gold/30 rounded-full text-jkd-gold text-sm tracking-widest uppercase">
            JKD PARTNER PROGRAM · 夥伴計劃
          </span>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
            你的客戶網絡，<span className="text-jkd-gold">就是你的被動收入</span>。
          </h1>
          <p className="text-lg text-gray-300">
            推薦成交，賺實收金額 10% 佣金。免費登記，WhatsApp 驗證，透明儀表板。
          </p>
        </div>

        {/* 規則三卡 */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="card-dark text-center p-5">
            <p className="text-3xl font-black text-jkd-gold mb-1">10%</p>
            <p className="text-sm text-gray-400">實收佣金（年滿 HK$50,000 成交升 12%）</p>
          </div>
          <div className="card-dark text-center p-5">
            <p className="text-3xl font-black text-jkd-gold mb-1">月結</p>
            <p className="text-sm text-gray-400">每月 1 號結算上月佣金（FPS／轉數快）</p>
          </div>
          <div className="card-dark text-center p-5">
            <p className="text-3xl font-black text-jkd-gold mb-1">終生綁定</p>
            <p className="text-sm text-gray-400">你帶來的客戶永遠記在你名下</p>
          </div>
        </div>

        {step === 'form' && (
          <div className="card-dark card-gold-border">
            <h2 className="text-xl font-bold text-white mb-2">免費登記 / Free Registration</h2>
            <p className="text-gray-400 text-sm mb-6">
              填寫後透過 WhatsApp 驗證身份即完成登記。登記後你會立即收到專屬推薦連結（審核通過後佣金生效）。
              <br />
              <span className="text-gray-500">Fill in your name and WhatsApp number — you will verify via WhatsApp and receive your referral link instantly.</span>
            </p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-gray-300 text-sm mb-1.5">姓名 / Name *</label>
                <input
                  required
                  type="text"
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1.5">WhatsApp 號碼 / WhatsApp Number *</label>
                <input
                  required
                  type="tel"
                  placeholder="852XXXXXXXX"
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              {msg && <p className="text-sm text-jkd-gold">{msg}</p>}
              <button type="submit" disabled={isSubmitting} className="w-full btn-gold flex items-center justify-center gap-2 disabled:opacity-70">
                {isSubmitting ? '處理中...' : '申請成為夥伴 / Apply'}
                <Send className="w-4 h-4" />
              </button>
              <p className="text-gray-500 text-xs text-center">
                提交即同意：個人資料僅用於本計劃結算與通知 / Your data is used only for this program's settlement and notices.
              </p>
            </form>
          </div>
        )}

        {step === 'verify' && (
          <div className="card-dark text-center">
            <div className="w-16 h-16 bg-jkd-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="w-8 h-8 text-jkd-gold" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">驗證你的 WhatsApp</h2>
            <p className="text-gray-400 mb-6">
              點擊下方按鈕打開 WhatsApp，發送驗證訊息完成登記。
              <br />
              <span className="text-gray-500">Click the button below to verify via WhatsApp.</span>
            </p>
            <a href={deepLink} target="_blank" rel="noopener noreferrer" className="w-full btn-gold flex items-center justify-center gap-2 mb-4">
              <MessageCircle className="w-5 h-5" />
              打開 WhatsApp / Open WhatsApp
            </a>
            <p className="text-gray-500 text-xs">
              發送後約 1 分鐘內會收到歡迎訊息與你的專屬推薦連結。
            </p>
            {msg && <p className="mt-4 text-sm text-jkd-gold">{msg}</p>}
          </div>
        )}

        {step === 'done' && (
          <div className="card-dark text-center">
            <CheckCircle className="w-16 h-16 text-jkd-gold mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3">登記完成！</h2>
            <p className="text-gray-400">請查看你的 WhatsApp。</p>
          </div>
        )}

        {/* 條款摘要 */}
        <div className="mt-10 card-dark p-6">
          <h3 className="text-white font-bold mb-3">計劃規則摘要 / Rules at a glance</h3>
          <ul className="space-y-2 text-sm text-gray-400">
            <li>· 佣金以<strong className="text-gray-300">實際收款金額</strong>計算；退款自動沖回。</li>
            <li>· 客戶首次經你的連結完成驗證即<strong className="text-gray-300">終生綁定</strong>在你名下（先到先得）。</li>
            <li>· 登記為<strong className="text-gray-300">審核制</strong>：通過後佣金生效；分享連結立即可用。</li>
            <li>· 每筆成交佣金個別確認（可按個案情況調整），每月 1 號結算上月佣金。</li>
            <li>· English: 10% of collected revenue (12% from HK$50k/yr); lifetime first-touch attribution; monthly settlement; application subject to review.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
